import { resolveClientRoute } from "@/main/navigation/ClientRouteRegistry";
// The eager classifier, never the lazy skeleton module (FI-3157 r158, `bootstrap/eagerGraphCuts.spec.ts`).
import { getNavigationTransitionKind } from "@/main/navigation/NavigationTransitionRoutes";

// Shared URL → prefetch-target resolution, kept in a leaf module so the prefetcher, the telemetry, and
// the navigation-commit consume hook all agree on "which component / which key does this URL map to"
// without an import cycle. See designs/01-now/2026-07-09-navigation-intent-route-prefetch-design.md.

// Server-document-swap routes are not in ClientRouteRegistry, but their destination component is still
// knowable from the URL — the same mapping the backend uses to emit the current page's data-component.
const TRANSITION_KIND_TO_COMPONENT: Record<string, string> = {
    discussion: "DiscussionPage",
    group: "GroupPage",
    series: "SeriesPage",
};

/** The lazy-mount component a soft navigation to {@code url} will render, or null if none applies. */
export function resolveDestinationComponent(url: URL): string | null {
    const clientRoute = resolveClientRoute(url);
    if (clientRoute) {
        return clientRoute.componentName;
    }
    const transitionKind = getNavigationTransitionKind(url);
    return transitionKind ? (TRANSITION_KIND_TO_COMPONENT[transitionKind] ?? null) : null;
}

/** Stable dedup/consume key for a destination (path + query). */
export function destinationKeyForUrl(url: URL): string {
    return `${url.pathname}${url.search}`;
}

/**
 * U44 (FI-3676, r156, ui-fundamentals §7c): WHICH data request a press on a link to {@code url} should start.
 * - `discussion-page-data`: the discussion's page-data cache (`ClientFactory.prefetchDiscussionPageData`), as before.
 * - `release-probe`: a client-route destination mounts with no document fetch, so its critical-path request is the
 *   frontend-release probe (`DocumentFetcher.prefetchFrontendReleaseProbe`). Every soft bottom-bar destination and a
 *   profile; never a route that mounts before its probe (the composer), whose probe is not on the critical path.
 * - `document`: a document-swap destination (a group, a series), whose critical-path request is its HTML document.
 */
export type PrefetchableData = "discussion-page-data" | "release-probe" | "document";

const RELEASE_PROBE_PREFETCH_ROUTES: ReadonlySet<string> = new Set([
    "home",
    "search",
    "conversations",
    "messages",
    "notifications",
    "user",
]);

// Not "group" (r156 landing review): a member's GET of a group page records the visit
// (`GroupController.viewGroup` → `groupVisitService.recordVisit`), clearing the group's new-posts dot and moving
// its "New since your last visit" divider, so a press that turns into a scroll would mark a visit that never
// happened. Add it back only once the server can tell a prefetch apart (e.g. `Sec-Purpose: prefetch`).
const DOCUMENT_PREFETCH_TRANSITION_KINDS: ReadonlySet<string> = new Set(["series"]);

/** The data request to start, and the exact address the navigation will request (so the handover keys match). */
export type PrefetchableDataTarget = { kind: PrefetchableData; requestUrl: URL };

export function resolvePrefetchableData(url: URL): PrefetchableDataTarget | null {
    const transitionKind = getNavigationTransitionKind(url);
    if (transitionKind === "discussion") {
        return { kind: "discussion-page-data", requestUrl: url };
    }
    const clientRoute = resolveClientRoute(url);
    if (clientRoute) {
        // The address `runEnhancedNavigation` probes for a plain tap (`releaseProbeUrl ?? url`).
        return RELEASE_PROBE_PREFETCH_ROUTES.has(clientRoute.routeId) && clientRoute.mountBeforeReleaseProbe !== true
            ? { kind: "release-probe", requestUrl: clientRoute.releaseProbeUrl ?? clientRoute.url }
            : null;
    }
    return transitionKind && DOCUMENT_PREFETCH_TRANSITION_KINDS.has(transitionKind)
        ? { kind: "document", requestUrl: url }
        : null;
}

/** Whether a press on a link to this route has a data request worth starting early (U44). */
export function hasPrefetchableData(url: URL): boolean {
    return resolvePrefetchableData(url) !== null;
}
